import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { downloadBlob, errMsg } from "@/lib/api";

const STATUS = {
  ativo: ["Ativo", "bg-emerald-50 text-emerald-700 ring-emerald-200"],
  experimental: ["Experimental", "bg-sky-50 text-sky-700 ring-sky-200"],
  inativo: ["Inativo", "bg-slate-100 text-slate-600 ring-slate-200"],
  suspenso: ["Suspenso", "bg-amber-50 text-amber-700 ring-amber-200"],
  cancelado: ["Cancelado", "bg-red-50 text-red-700 ring-red-200"],
  aprovado: ["Aprovado", "bg-emerald-50 text-emerald-700 ring-emerald-200"],
  pendente: ["Pendente", "bg-amber-50 text-amber-700 ring-amber-200"],
  atrasado: ["Em atraso", "bg-red-50 text-red-700 ring-red-200"],
  recusado: ["Recusado", "bg-red-50 text-red-700 ring-red-200"],
  estornado: ["Estornado", "bg-violet-50 text-violet-700 ring-violet-200"],
  contestado: ["Contestado", "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200"],
  active: ["Assinatura ativa", "bg-emerald-50 text-emerald-700 ring-emerald-200"],
  trialing: ["Em teste", "bg-sky-50 text-sky-700 ring-sky-200"],
  past_due: ["Cobrança em atraso", "bg-red-50 text-red-700 ring-red-200"],
  canceled: ["Cancelada", "bg-slate-100 text-slate-600 ring-slate-200"],
  incomplete: ["Incompleta", "bg-amber-50 text-amber-700 ring-amber-200"],
  unpaid: ["Não paga", "bg-red-50 text-red-700 ring-red-200"],
  pago: ["Pago", "bg-emerald-50 text-emerald-700 ring-emerald-200"],
};

export function StatusBadge({ status, testid }) {
  const [label, cls] = STATUS[status] || [status || "—", "bg-slate-100 text-slate-600 ring-slate-200"];
  return (
    <span data-testid={testid} className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ring-1 ${cls}`}>
      {label}
    </span>
  );
}

export const payStatus = (p, today) => (p.status === "pendente" && p.due_date < today ? "atrasado" : p.status);

export function PageHeader({ eyebrow, title, children }) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-6 fade-up">
      <div>
        {eyebrow && <div className="eyebrow mb-1">{eyebrow}</div>}
        <h1 className="text-3xl sm:text-4xl font-extrabold uppercase text-zinc-900" data-testid="page-title">{title}</h1>
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

export function Kpi({ label, value, hint, icon: Icon, tone = "default", testid }) {
  const tones = { default: "text-zinc-900", red: "text-red-600", green: "text-emerald-600", amber: "text-amber-600" };
  return (
    <div className="panel p-4 sm:p-5 fade-up hover:-translate-y-0.5 transition-transform duration-200" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
      </div>
      <div className={`font-display text-3xl sm:text-4xl font-bold mt-2 ${tones[tone]}`}>{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

export function Field({ label, children, className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      {children}
    </div>
  );
}

export function SimpleSelect({ value, onChange, options, placeholder = "Selecione", testid, className = "" }) {
  return (
    <Select value={value ?? ""} onValueChange={onChange}>
      <SelectTrigger className={className} data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} data-testid={testid ? `${testid}-opt-${o.value}` : undefined}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function Empty({ text = "Nenhum registro encontrado." }) {
  return <div className="text-sm text-slate-500 py-10 text-center" data-testid="empty-state">{text}</div>;
}

export function ExportButton({ kind, start, end, label = "Exportar CSV" }) {
  const go = async () => {
    try {
      const qs = new URLSearchParams({ ...(start && { start }), ...(end && { end }) }).toString();
      await downloadBlob(`/reports/export/${kind}?${qs}`, `${kind}.csv`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Button variant="outline" size="sm" onClick={go} data-testid={`export-${kind}-btn`}>
      <Download className="w-4 h-4 mr-1.5" /> {label}
    </Button>
  );
}

export const Th = ({ children, className = "" }) => (
  <th className={`text-left text-[11px] font-bold uppercase tracking-wider text-slate-500 px-3 py-2.5 ${className}`}>{children}</th>
);
export const Td = ({ children, className = "" }) => <td className={`px-3 py-2.5 text-sm ${className}`}>{children}</td>;

export function Table({ children, testid }) {
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full min-w-[640px]">{children}</table>
    </div>
  );
}

export const Tr = ({ children, onClick, testid }) => (
  <tr onClick={onClick} data-testid={testid}
    className={`border-t border-slate-100 transition-colors ${onClick ? "cursor-pointer hover:bg-red-50/40" : "hover:bg-slate-50/60"}`}>
    {children}
  </tr>
);
